/**
 * Element Transform Utils
 *
 * Deep-clones a BIM element and applies a rigid XY transform (translate /
 * rotate about a centre) to every geometry point field it carries. Used by
 * the array tools so every category (walls, MEP runs, components, openings)
 * participates in arrays — not just walls.
 */

import * as THREE from 'three';

export interface ElementTransform {
    /** Translation offset (world). */
    translate?: [number, number, number];
    /** Rotation around Z at `center` (radians, CCW). */
    rotate?: { center: THREE.Vector3; angleRad: number };
}

/** Point-valued geometryData fields (number triples). */
const TRIPLE_KEYS = ['start', 'end', 'location', 'position', 'origin', 'center', 'midPoint'] as const;
/** Polyline-valued fields (arrays of triples). */
const POLYLINE_KEYS = ['path', 'points', 'controlPoints', 'vertices'] as const;
/** Polygon-valued fields (arrays of [x, y] pairs, z rides separately). */
const PAIR_POLYGON_KEYS = ['boundary'] as const;

function isNumericArray(v: unknown, len: number): v is number[] {
    return Array.isArray(v) && v.length >= len && typeof v[0] === 'number' && typeof v[1] === 'number';
}

function rotateXy(x: number, y: number, center: THREE.Vector3, cos: number, sin: number): [number, number] {
    const dx = x - center.x;
    const dy = y - center.y;
    return [center.x + dx * cos - dy * sin, center.y + dx * sin + dy * cos];
}

/**
 * Clone `element` with the given rigid transform baked into its geometry.
 * The source element is never mutated. Known point/polyline/polygon fields
 * are transformed; scalar fields ride along untouched.
 */
export function transformElementCopy<T extends { id: string; geometryData?: any; parameters?: Record<string, any>; constraints?: any[] }>(
    element: T,
    transform: ElementTransform,
    newId: string
): T {
    const clone: T = JSON.parse(JSON.stringify(element));
    clone.id = newId;

    const g = (clone as any).geometryData;
    if (g && typeof g === 'object') {
        const cos = transform.rotate ? Math.cos(transform.rotate.angleRad) : 1;
        const sin = transform.rotate ? Math.sin(transform.rotate.angleRad) : 1;
        const center = transform.rotate?.center;

        const movePoint = (p: number[]): void => {
            if (transform.translate) {
                p[0] += transform.translate[0];
                if (p.length > 1) p[1] += transform.translate[1];
                if (p.length > 2) p[2] += transform.translate[2];
            }
            if (transform.rotate && center) {
                const [rx, ry] = rotateXy(p[0], p[1], center, cos, sin);
                p[0] = rx;
                p[1] = ry;
            }
        };

        for (const key of TRIPLE_KEYS) {
            const p = g[key];
            if (isNumericArray(p, 2)) movePoint(p);
        }
        for (const key of POLYLINE_KEYS) {
            const poly = g[key];
            if (Array.isArray(poly)) {
                for (const p of poly) {
                    if (isNumericArray(p, 2)) movePoint(p);
                }
            }
        }
        for (const key of PAIR_POLYGON_KEYS) {
            const poly = g[key];
            if (Array.isArray(poly)) {
                for (const p of poly) {
                    if (isNumericArray(p, 2)) movePoint(p);
                }
            }
        }
        // Authored yaw angles (degrees) rotate with the body.
        if (transform.rotate && typeof g.rotation === 'number') {
            g.rotation += THREE.MathUtils.radToDeg(transform.rotate.angleRad);
        }
    }

    return clone;
}
